import { Editor, Element, Node, type NodeEntry, Text, Transforms } from 'slate';
import {
  type EditorPlugin,
  type TableCellElement,
  type TableElement,
  type TableRowElement,
} from '../../types/aha-editor';
import { getTableStructureFromElement } from './TableStructure';
import {
  isTableCellElement,
  isTableElement,
  isTableRowElement,
} from '../../types/guards';
import emptyParagraph from '../../AhaEditorUtils/util/emptyParagraph';

// Normalize - ensure our table is a valid shape.
//
// 1. Table with no rows should be removed.
// 2. If all rows have no cells, remove the table.
// 3. If the first row in the table has no cells, remove the row.
// 4. All cells must contain a block element. Paragraph will be added if necessary.
// 5. All rows must have the same number of cells.
// 6. Children of tables are only rows.
// 7. Children of rows are only cells (or a single text element for merged rows).
// 8. Table rows should only exist within tables.
// 9. Table cells should only exist within a table row.
// 10. Check column widths property length matches table width.
// 11. TODO: Check that properties are well-formed.

const TableNormalizationPlugin: EditorPlugin = {
  name: 'Table Normalizations',
  normalizations: [
    {
      type: 'table',
      run: (editor: Editor, entry: NodeEntry<TableElement>) => {
        const [node, path] = entry;

        // 1. Remove empty tables.
        if (node.children.length === 0) {
          Transforms.removeNodes(editor, { at: path });
          return true;
        }

        // 2. Remove tables with no cells.
        let hasCells = false;
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i];
          if (isTableRowElement(child)) {
            // 3. We use empty trs for merged cells, but it causes major
            // problems if the first row is empty. It's not really
            // valid, either, because nothing can rowspan into it. If
            // the first row has no cells, we'll remove it.
            if (i === 0) {
              if (
                child.children.length === 1 &&
                Text.isText(child.children[0])
              ) {
                Transforms.removeNodes(editor, { at: [...path, 0] });
                return true;
              }
            }

            if (child.children.length > 0) {
              hasCells = true;
              break;
            }
          } else {
            // 6. Children of tables must be rows.
            Transforms.removeNodes(editor, { at: [...path, i] });
            return true;
          }
        }
        if (!hasCells) {
          Transforms.removeNodes(editor, { at: path });
          return true;
        }

        // 5. All rows must have the same number of cells.
        const tableStructure = getTableStructureFromElement(editor, node);
        const missingCells = tableStructure.missingCellPositions();

        if (missingCells.length > 0) {
          Editor.withoutNormalizing(editor, () => {
            missingCells.forEach(({ columnIndex, rowIndex }) => {
              const insertIndex = tableStructure.actualColumnIndex(
                columnIndex,
                rowIndex
              );

              const rowPath = [...path, rowIndex];

              // Insert a row if one doesn't already exist
              if (!Node.has(editor, rowPath)) {
                Transforms.insertNodes(
                  editor,
                  {
                    type: 'table-row',
                    children: [],
                  },
                  { at: rowPath }
                );
              }

              Transforms.insertNodes(
                editor,
                {
                  type: 'table-cell',
                  children: [emptyParagraph()],
                },
                { at: [...rowPath, insertIndex] }
              );
            });
          });
          return true;
        }

        // 10. Check columnWidths property length matches table width.
        // If there is an inconsistency, remove the property altogether,
        // resulting in evenly distributed columns.
        if (
          node.table_columnWidths &&
          node.table_columnWidths.length !== tableStructure.width
        ) {
          Transforms.unsetNodes(editor, 'table_columnWidths', { at: path });
          return true;
        }
      },
    },
    {
      type: 'table-row',
      run: (editor: Editor, entry: NodeEntry<TableRowElement>) => {
        const path = entry[1];
        const parent = Editor.parent(editor, path);

        // 8. Table rows should only exist within tables.
        if (!parent[0] || !isTableElement(parent[0])) {
          Transforms.unwrapNodes(editor, { at: path });
          return true;
        }
      },
    },
    {
      type: 'table-row',
      run: (editor: Editor, entry: NodeEntry<TableRowElement>) => {
        const [node, path] = entry;

        // 7. Children of rows are only cells (or a single text element for merged rows).
        for (let i = node.children.length - 1; i >= 0; i--) {
          if (
            !isTableCellElement(node.children[i]) &&
            !Text.isText(node.children[i])
          ) {
            Transforms.removeNodes(editor, {
              at: [...path, i],
            });
            return true;
          }
        }
      },
    },
    {
      type: 'table-cell',
      run: (editor: Editor, entry: NodeEntry<TableCellElement>) => {
        const [node, path] = entry;

        // 4. All cells must contain a block element. Paragraph will be added if necessary.
        const hasChildren = node.children.length > 0;
        const hasBlock = node.children.some(
          (child) => Element.isElement(child) && Editor.isBlock(editor, child)
        );
        if (!hasChildren) {
          Transforms.insertNodes(editor, emptyParagraph(), {
            at: [...path, 0],
          });
          return true;
        }
        if (!hasBlock) {
          Transforms.wrapNodes(
            editor,
            { type: 'paragraph', children: [] },
            { at: [...path, 0] }
          );
          return true;
        }
      },
    },
    {
      type: 'table-cell',
      run: (editor: Editor, entry: NodeEntry<TableCellElement>) => {
        const path = entry[1];
        const parent = Editor.parent(editor, path);

        // 9. Table cells should only exist within a table row.
        if (!parent[0] || !isTableRowElement(parent[0])) {
          Transforms.unwrapNodes(editor, { at: path });
          return true;
        }
      },
    },
  ],
};

export default TableNormalizationPlugin;
